/**
 * Search Types
 *
 * Centralized TypeScript types for search functionality.
 * Consolidated from multiple locations to prevent duplication.
 */

// ===========================
// Search Result Types
// ===========================

/**
 * User search result with profile information
 */
export interface UserSearchResult {
	id: string;
	username: string | null;
	display_name: string;
	manual_avatar_url: string | null; // Manually uploaded images (highest priority)
	google_avatar_url: string | null; // Google OAuth profile images (fallback)
	avatar_source?: string | null;
	is_private: boolean;
}

/**
 * Festival search result with event details
 */
export interface FestivalSearchResult {
	id: string;
	name: string;
	slug: string;
	location: string;
	venue_obj?: {
		id: string;
		name: string;
		slug: string;
		city: string;
		state?: string | null;
		country: string;
		venue_type?: string | null;
		capacity?: number | null;
	} | null;
	start_date: string;
	end_date: string;
	poster_url: string | null;
	alias?: string | null;
}

/**
 * Artist search result with genre information
 */
export interface ArtistSearchResult {
	id: string;
	name: string;
	slug?: string;
	image_url: string | null;
	spotify_genres: string[] | null;
	manual_genres: string[] | null;
	is_collaborative_alias?: boolean;
	is_group_alias?: boolean;
	group_slug?: string;
	primary_artist_name?: string;
	collaborator_names?: string[];
}

/**
 * Show search result with concert details
 */
export interface ShowSearchResult {
	id: string;
	name: string;
	slug: string;
	venue_obj?: {
		id: string;
		name: string;
		slug: string;
		city: string;
		state?: string | null;
		country: string;
		venue_type?: string | null;
		capacity?: number | null;
	} | null;
	date: string;
	main_artist: string;
	main_artist_image_url?: string | null;
	poster_url: string | null;
	location: string;
}

// ===========================
// API Types
// ===========================

/**
 * Search API response containing all result types
 */
export interface SearchResponse {
	users: UserSearchResult[];
	festivals: FestivalSearchResult[];
	artists: ArtistSearchResult[];
	shows: ShowSearchResult[];
	total: number;
	message?: string;
}

/**
 * Search request parameters
 */
export interface SearchRequest {
	query: string;
	limit?: number;
	filters?: SearchFilters;
}

/**
 * Search filters for advanced queries
 */
export interface SearchFilters {
	type?: ("users" | "festivals" | "artists" | "shows")[];
	dateRange?: {
		start: string;
		end: string;
	};
	genres?: string[];
}

/**
 * Search options for hooks and components
 */
export interface SearchOptions {
	limit?: number;
	enabled?: boolean;
	debounceMs?: number;
	minQueryLength?: number;
}

// ===========================
// UI Component Types
// ===========================

/**
 * Props for the main search component
 */
export interface TopNavSearchProps {
	onNavigateToFestival?: (festivalSlug: string) => void;
	// Enhanced hooks - optional for better performance
	useSearchState?: () => UseSearchStateReturn;
	useSearchHook?: UseSearchHook;
}

/**
 * Type for custom search hook implementations
 * Should return a tRPC query result compatible with the standard search hook
 */
export type UseSearchHook = (
	query: string,
	options?: { enabled: boolean },
) => {
	data?: SearchResponse;
	isLoading?: boolean;
	error?: Error | null;
	[key: string]: unknown;
};

/**
 * Props for the search dialog component
 */
export interface SearchDialogProps {
	isOpen: boolean;
	onClose: () => void;
	defaultQuery?: string;
}

/**
 * Props for individual search result components
 */
export interface SearchResultItemProps<T> {
	result: T;
	isSelected: boolean;
	onSelect: () => void;
}

// ===========================
// Hook Return Types
// ===========================

/**
 * Search state hook return type
 */
export interface UseSearchStateReturn {
	isSearchOpen: boolean;
	searchValue: string;
	selectedSearchIndex: number;
	setSearchOpen: (isOpen: boolean) => void;
	setSearchValue: (value: string) => void;
	setSelectedSearchIndex: (index: number) => void;
	resetSearchState: () => void;
	incrementSearchIndex: (totalResults: number) => void;
	decrementSearchIndex: (totalResults: number) => void;
	resetSearchIndex: () => void;
}

/**
 * Search data hook return type
 */
export interface UseSearchReturn {
	userResults: UserSearchResult[];
	festivalResults: FestivalSearchResult[];
	artistResults: ArtistSearchResult[];
	showResults?: ShowSearchResult[];
	isLoading: boolean;
	error: string | null;
}

/**
 * Search results hook return type with computed values
 */
export interface UseSearchResultsReturn extends UseSearchReturn {
	totalResults: number;
	hasResults: boolean;
	isEmpty: boolean;
}

/**
 * Keyboard navigation hook options
 */
export interface UseSearchKeyboardNavOptions {
	totalResults: number;
	selectedIndex: number;
	onSelectedIndexChange: (index: number) => void;
	onSelect: (index: number) => void;
	onClose: () => void;
}

/**
 * Keyboard navigation hook return type
 */
export interface UseSearchKeyboardNavReturn {
	selectedIndex: number;
	handleKeyDown: (e: KeyboardEvent) => void;
	resetSelection: () => void;
}

// ===========================
// Query Keys
// ===========================

/**
 * TanStack Query keys for search caching
 */
export const searchKeys = {
	all: ["search"] as const,
	results: (query: string, options?: { limit?: number }) =>
		[...searchKeys.all, "results", query, options] as const,
} as const;
